import { useState, useEffect } from 'react';
import { FaImage, FaMagic, FaUpload, FaSyncAlt } from 'react-icons/fa';

const IMAGE_ENDPOINT = 'https://torlanco-getimageendpoint.web.val.run/?prompt=';

const ProductImageManager = ({ 
  productName, 
  categoryName, 
  initialImage,
  onImageChange 
}) => {
  const [imageType, setImageType] = useState('generate'); // 'generate' o 'upload'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  // Generar sugerencias de prompts basadas en el producto y categoría
  const generatePromptSuggestions = () => {
    const suggestions = [
      `professional product photo of ${productName} for ${categoryName} category, clean medical supplement bottle on white background, professional studio lighting, 4k quality`,
      `${productName} supplement bottle, minimalist design, pharmaceutical grade presentation, clean white background, professional product photography, 4k`,
      `modern health supplement ${productName}, premium packaging, clinical presentation, ${categoryName.toLowerCase()} product line, studio lighting, 4k resolution`
    ];
    return suggestions;
  };

  useEffect(() => {
    // Si hay una imagen inicial, determinar su tipo y configurar el estado
    if (initialImage) {
      if (initialImage.type === 'generated') {
        setImageType('generate');
        setSelectedPrompt(initialImage.prompt || '');
        setGeneratedImage(initialImage.url);
      } else {
        setImageType('upload');
        setUploadPreview(initialImage.url);
      }
    }
  }, [initialImage]);

  // Manejar cambio de archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
        onImageChange({
          type: 'uploaded',
          file: file,
          url: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Generar imagen usando el prompt
  const generateImage = async (prompt) => {
    setIsGenerating(true);
    try {
      const encodedPrompt = encodeURIComponent(prompt);
      const imageUrl = `${IMAGE_ENDPOINT}${encodedPrompt}`;
      setGeneratedImage(imageUrl);
      setSelectedPrompt(prompt);
      onImageChange({
        type: 'generated',
        url: imageUrl,
        prompt: prompt
      });
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h5 className="card-title mb-4">Imagen del Producto</h5>
        
        {/* Selector de tipo de imagen */}
        <div className="btn-group w-100 mb-4" role="group">
          <input
            type="radio"
            className="btn-check"
            name="imageType"
            id="generateBtn"
            checked={imageType === 'generate'}
            onChange={() => setImageType('generate')}
          />
          <label className="btn btn-outline-primary" htmlFor="generateBtn">
            <FaMagic className="me-2" />
            Generar Imagen
          </label>

          <input
            type="radio"
            className="btn-check"
            name="imageType"
            id="uploadBtn"
            checked={imageType === 'upload'}
            onChange={() => setImageType('upload')}
          />
          <label className="btn btn-outline-primary" htmlFor="uploadBtn">
            <FaUpload className="me-2" />
            Subir Imagen
          </label>
        </div>

        {/* Sección de subida de imagen */}
        {imageType === 'upload' && (
          <div className="upload-section">
            <div className="mb-3">
              <label className="form-label">Seleccionar Imagen</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>
            {uploadPreview && (
              <div className="image-preview mb-3">
                <img
                  src={uploadPreview}
                  alt="Preview"
                  className="img-fluid rounded"
                  style={{ maxHeight: '200px' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Sección de generación de imagen */}
        {imageType === 'generate' && (
          <div className="generate-section">
            {/* Sugerencias de prompts */}
            <div className="mb-3">
              <label className="form-label">Sugerencias de Descripción</label>
              <div className="d-grid gap-2">
                {generatePromptSuggestions().map((prompt, index) => (
                  <button
                    key={index}
                    className={`btn btn-outline-secondary text-start ${
                      selectedPrompt === prompt ? 'active' : ''
                    }`}
                    onClick={() => generateImage(prompt)}
                    disabled={isGenerating}
                  >
                    <small>{prompt}</small>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt personalizado */}
            <div className="mb-3">
              <label className="form-label">Descripción Personalizada</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Describe cómo quieres que se vea la imagen..."
                />
                <button
                  className="btn btn-primary"
                  onClick={() => generateImage(customPrompt)}
                  disabled={!customPrompt || isGenerating}
                >
                  {isGenerating ? (
                    <FaSyncAlt className="spinner" />
                  ) : (
                    <FaMagic />
                  )}
                </button>
              </div>
            </div>

            {/* Preview de imagen generada */}
            {generatedImage && (
              <div className="image-preview mb-3">
                <img
                  src={generatedImage}
                  alt="Generated Preview"
                  className="img-fluid rounded"
                  style={{ maxHeight: '200px' }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductImageManager;
